import {Component, inject, signal, viewChild} from '@angular/core';
import {saveAs} from 'file-saver';
import {CommonModule} from '@angular/common';
import {DxDataGridComponent, DxDataGridModule, DxToolbarModule} from 'devextreme-angular';
import {SharedModule} from '../../../shared/shared.module';
import {AvoirExterneService} from '../../../core/services/entities/avoir-externe.service';
import {AvoirExterneDTO} from '../../../core/dtos/facturation/fournisseurs/avoirs/avoir-externe-dto';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';
import {AvoirExterneDialogComponent} from './avoir-externe-dialog/avoir-externe-dialog.component';
import {RapprochementAvoirExterneDialogComponent} from './rapprochement-avoir-externe-dialog/rapprochement-avoir-externe-dialog.component';
import {CegidExportErrorsDialogComponent} from '../cegid/cegid-export-errors-dialog/cegid-export-errors-dialog.component';
import {CegidExportErrorDTO} from '../../../core/dtos/facturation/fournisseurs/cegid/cegid-export-error-dto';
import {AvoirExterneStatutEnum, AVOIR_EXTERNE_STATUT_LABELS} from '../../../core/enums/facturation/fournisseur/avoir-externe-statut-enum';
import {Auth2Service} from '../../../core/services/security/auth2.service';
import {RapprochementAvoirExterneStatutEnum, RAPPROCHEMENT_AVOIR_EXTERNE_STATUT_LABELS} from '../../../core/enums/facturation/fournisseur/rapprochement-avoir-externe-statut-enum';
import {UtilsService} from '../../../core/utils/utils.service';
import {ToastService} from '../../../core/services/technique/toast.service';
import {HAS_EXPORT_CEGID, MSG_KEY, MSG_SEVERITY} from '../../../core/constants';

@Component({
  selector: 'yo-avoirs-externes',
  templateUrl: './avoirs-externes.component.html',
  styleUrls: ['./avoirs-externes.component.scss'],
  standalone: true,
  imports: [CommonModule, DxDataGridModule, DxToolbarModule, SharedModule, AvoirExterneDialogComponent, RapprochementAvoirExterneDialogComponent, CegidExportErrorsDialogComponent],
  providers: [AvoirExterneService]
})
export class AvoirsExternesComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;
  protected readonly AvoirExterneStatutEnum = AvoirExterneStatutEnum;
  protected readonly RapprochementAvoirExterneStatutEnum = RapprochementAvoirExterneStatutEnum;

  readonly statutList = Object.values(AvoirExterneStatutEnum).map(value => ({
    value,
    label: AVOIR_EXTERNE_STATUT_LABELS[value]
  }));

  readonly rapprochementStatutList = Object.values(RapprochementAvoirExterneStatutEnum).map(value => ({
    value,
    label: RAPPROCHEMENT_AVOIR_EXTERNE_STATUT_LABELS[value]
  }));

  readonly avoirExterneSvc = inject(AvoirExterneService);
  private readonly auth2Svc = inject(Auth2Service);
  private readonly utilsSvc = inject(UtilsService);
  private readonly toastSvc = inject(ToastService);

  readonly hasExportCegid: boolean = this.auth2Svc.hasFeature(this.auth2Svc.utilisateur, HAS_EXPORT_CEGID);

  readonly dateReceptionSelectedDates: Date[] = [];

  readonly showDialog = signal(false);
  readonly selectedAvoirId = signal<number | null>(null);
  readonly selectedDialogTitle = signal('Avoir externe');
  readonly selectedAvoirIdList = signal<number[]>([]);

  readonly showRapprochementDialog = signal(false);
  readonly selectedAvoirIdForRapprochement = signal<number | null>(null);
  readonly selectedAvoirNumeroForRapprochement = signal<string | null>(null);
  readonly selectedAvoirSiteIdForRapprochement = signal<number | null>(null);

  readonly showCegidErrorsDialog = signal(false);
  readonly cegidErrorList = signal<CegidExportErrorDTO[]>([]);

  private readonly grid = viewChild.required(DxDataGridComponent);
  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  getStatutLabel = (rowData: AvoirExterneDTO): string =>
    AVOIR_EXTERNE_STATUT_LABELS[rowData.statut as AvoirExterneStatutEnum] ?? rowData.statut;

  getRapprochementStatutLabel = (rowData: AvoirExterneDTO): string =>
    RAPPROCHEMENT_AVOIR_EXTERNE_STATUT_LABELS[rowData.rapprochementStatut as RapprochementAvoirExterneStatutEnum] ?? rowData.rapprochementStatut;

  getAvoirNumeroList = (data: AvoirExterneDTO): string[] => {
    if (!data?.rapprochementList?.length) return [];
    return data.rapprochementList
      .map(r => r.avoirNumero)
      .filter(n => n?.trim());
  };

  exportCegid = (): void => {
    const avoirIdList = this.selectedAvoirIdList();
    const filters = avoirIdList.length ? { avoirIdList } : {};
    this.avoirExterneSvc.exportCegidFactureAvoir(filters).subscribe(result => {
      if (result?.erreurList?.length) {
        this.cegidErrorList.set(result.erreurList);
        this.showCegidErrorsDialog.set(true);
        return;
      }
      if (!result?.fichier) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, 'Aucun élément à exporter');
        return;
      }
      const byteCharacters = atob(result.fichier);
      const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
      const blob = new Blob([new Uint8Array(byteNumbers)], {type: 'application/octet-stream'});
      saveAs(blob, `factures-avoirs-cegid.tra`);
    });
  };

  getAvoirNumeroJoined = (data: AvoirExterneDTO): string =>
    this.getAvoirNumeroList(data).join(' ');

  onDateReceptionChange = (dates: Date[] | undefined): void => {
    if (!dates) {
      this.avoirExterneSvc.filtersSignal.update(f => ({ ...f, dateReceptionDebut: null, dateReceptionFin: null, page: 0 }));
      return;
    }
    this.avoirExterneSvc.filtersSignal.update(f => ({
      ...f,
      dateReceptionDebut: this.utilsSvc.formatToIsoDate(dates[0]),
      dateReceptionFin: this.utilsSvc.formatToIsoDate(dates[1]),
      page: 0
    }));
  };

  onSelectionChanged = (e: any): void => {
    const idList: number[] = (e.selectedRowsData as AvoirExterneDTO[]).map(r => r.id);
    this.selectedAvoirIdList.set(idList);
  };

  openDialog = (dto: AvoirExterneDTO): void => {
    this.selectedAvoirId.set(dto.id);
    this.selectedDialogTitle.set(dto.numero ? `Avoir externe — ${dto.numero}` : 'Avoir externe');
    this.showDialog.set(true);
  };

  openRapprochementDialog = (dto: AvoirExterneDTO): void => {
    this.selectedAvoirIdForRapprochement.set(dto.id);
    this.selectedAvoirNumeroForRapprochement.set(dto.numero);
    this.selectedAvoirSiteIdForRapprochement.set(dto.idSite);
    this.showRapprochementDialog.set(true);
  };

  onEditorPreparing = (e: any): void => {
    if (e.parentType !== 'filterRow') return;
    if (e.dataField === 'statut') {
      e.editorName = 'dxSelectBox';
      e.editorOptions = { ...e.editorOptions, dataSource: null, items: this.statutList, valueExpr: 'value', displayExpr: 'label', showClearButton: true };
    } else if (e.dataField === 'rapprochementStatut') {
      e.editorName = 'dxSelectBox';
      e.editorOptions = { ...e.editorOptions, dataSource: null, items: this.rapprochementStatutList, valueExpr: 'value', displayExpr: 'label', showClearButton: true };
    }
  };

  onGridOptionChanged = (e: any): void => {
    if (!e.fullName) return;

    if (e.fullName === 'paging.pageIndex') {
      this.avoirExterneSvc.filtersSignal.update(f => ({ ...f, page: e.value }));
      return;
    }

    if (e.fullName === 'paging.pageSize') {
      this.avoirExterneSvc.filtersSignal.update(f => ({ ...f, limit: e.value, page: 0 }));
      return;
    }

    if (!e.fullName.includes('filterValue')) return;
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
    this.filterDebounceTimer = setTimeout(() => {
      const numero = (this.grid().instance.columnOption('numero', 'filterValue') as string) || null;
      const siteExterneLibelle = (this.grid().instance.columnOption('siteExterneLibelle', 'filterValue') as string) || null;
      const avoirNumero = (this.grid().instance.columnOption('avoirNumeroRecherche', 'filterValue') as string) || null;
      const statut = (this.grid().instance.columnOption('statut', 'filterValue') as string) || null;
      const rapprochementStatut = (this.grid().instance.columnOption('rapprochementStatut', 'filterValue') as string) || null;
      const dateFacture = this.utilsSvc.formatToIsoDate(this.grid().instance.columnOption('dateFacture', 'filterValue') as Date | null);
      const dateRapprochement = this.utilsSvc.formatToIsoDate(this.grid().instance.columnOption('dateRapprochement', 'filterValue') as Date | null);
      const factureBloquanteRaw = this.grid().instance.columnOption('factureBloquante', 'filterValue');
      const factureBloquante = factureBloquanteRaw === true || factureBloquanteRaw === false ? factureBloquanteRaw : null;
      void this.grid().instance.pageIndex(0);
      this.avoirExterneSvc.filtersSignal.update(f => ({ ...f, numero, siteExterneLibelle, avoirNumero, statut, rapprochementStatut, dateFacture, dateRapprochement, factureBloquante, page: 0 }));
    }, 400);
  };
}
